import { useEffect, useMemo, useState } from "react"

import type {
  OperationsDashboardV1,
  OperationsQueueKindV1,
} from "@impetusos/contracts"

import {
  ConsoleRequestError,
  readOperationsDashboard,
} from "../transport/foundation-console.js"

const labels: Record<OperationsQueueKindV1, string> = {
  dispatch_freshness: "Dispatch freshness",
  offline_backlog: "Offline backlog",
  missing_gps: "Missing GPS",
  completion_exception: "Completion review",
  billing_exception: "Billing review",
  support_sla: "Support SLA",
  provider_failure: "Provider failure",
}

const age = (seconds: number): string => {
  if (seconds < 60) return "under a minute"
  if (seconds < 3_600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86_400) return `${Math.floor(seconds / 3_600)}h`
  return `${Math.floor(seconds / 86_400)}d`
}

export const OperationsDashboard = () => {
  const [dashboard, setDashboard] = useState<OperationsDashboardV1 | null>(null)
  const [filter, setFilter] = useState<OperationsQueueKindV1 | "all">("all")
  const [status, setStatus] = useState<"loading" | "ready" | "denied" | "retry">(
    "loading",
  )

  useEffect(() => {
    let active = true
    void readOperationsDashboard()
      .then((value) => {
        if (!active) return
        setDashboard(value)
        setStatus("ready")
      })
      .catch((error: unknown) => {
        if (!active) return
        setStatus(
          error instanceof ConsoleRequestError && error.kind === "denied"
            ? "denied"
            : "retry",
        )
      })
    return () => {
      active = false
    }
  }, [])

  const queue = useMemo(
    () =>
      dashboard?.queues.filter((item) => filter === "all" || item.kind === filter)
        ?? [],
    [dashboard, filter],
  )
  const summary = dashboard?.summary

  return (
    <section className="operations-dashboard" id="operations" aria-labelledby="operations-title">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Branch-scoped monitoring</p>
          <h2 id="operations-title">Operations dashboard</h2>
        </div>
        {dashboard && (
          <span className="status-chip">
            {dashboard.complete ? "Bounded view complete" : "Top 100 · filters required"}
          </span>
        )}
      </div>
      {status === "loading" && <p role="status">Loading operations queues…</p>}
      {status === "denied" && <p role="alert">Your current role cannot view operations queues.</p>}
      {status === "retry" && <p role="alert">Operations queues are temporarily unavailable.</p>}
      {status === "ready" && dashboard && (
        <>
          <div className="operations-summary" aria-label="Exception counts">
            <div><strong>{summary?.dispatchFreshness}</strong><span>Dispatch stale</span></div>
            <div><strong>{summary?.offlineBacklog}</strong><span>Offline backlog</span></div>
            <div><strong>{summary?.missingGps}</strong><span>Missing GPS</span></div>
            <div><strong>{summary?.completionExceptions}</strong><span>Completion</span></div>
            <div><strong>{summary?.billingExceptions}</strong><span>Billing</span></div>
            <div><strong>{summary?.supportSla}</strong><span>Support SLA</span></div>
            <div><strong>{summary?.providerFailures}</strong><span>Providers</span></div>
          </div>
          <div className="operations-toolbar">
            <label>
              Queue
              <select
                value={filter}
                onChange={(event) =>
                  setFilter(event.target.value as OperationsQueueKindV1 | "all")
                }
              >
                <option value="all">All queues</option>
                {Object.entries(labels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
            <p>
              Offline count includes commands observed by the server as retry,
              blocked, or conflicted. Commands still only on a device appear
              after its next sync attempt.
            </p>
          </div>
          {queue.length === 0 ? (
            <p className="operations-clear">No items in this queue.</p>
          ) : (
            <div className="operations-table-wrap">
              <table className="operations-table">
                <thead>
                  <tr><th>Queue</th><th>Status</th><th>Branch</th><th>Age</th><th>Reference</th></tr>
                </thead>
                <tbody>
                  {queue.map((item) => (
                    <tr key={item.itemId}>
                      <td><span className={`queue-kind kind-${item.kind}`}>{labels[item.kind]}</span></td>
                      <td><strong>{item.reasonCode.replaceAll("_", " ")}</strong><small>{item.state}</small></td>
                      <td>{item.branchId?.slice(0, 8) ?? "Tenant-wide"}</td>
                      <td>{age(item.ageSeconds)}</td>
                      <td><code>{item.sourceEntityId.slice(0, 8)}</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  )
}
